import { z } from 'zod';

import { BOOKING_DEFAULTS } from '../constants/booking';
import {
  LevelSchema,
  OpenSeatKind,
  OpenSeatKindSchema,
  OpenSeatStatusSchema,
  TournamentFormatSchema,
  WeekdaySchema,
} from '../enums';
import { IdSchema, KopecksSchema } from './common';
import { ActivityBriefSchema } from './pool';

/**
 * Открытые места, замены и пробный сбор (BLUEPRINT Stage 11, контракт 6:
 * `openSeats.list`, `openSeats.claim(seatId)`, `admin.seats.*`).
 * Витрина показывает слот, а не людей: активность, вечер, район, уровень, цена.
 */

/**
 * Что показывает витрина: место в отряде, место запасного, замена на сбор, пробный сбор и
 * (Stage 17, только с `feature.myGatherings`) разовое место на сбор готового отряда.
 */
export const SeatOfferKindSchema = z.enum(['SEASON', 'RESERVE', 'SUBSTITUTE', 'TRIAL', 'SINGLE']);
export type SeatOfferKind = z.infer<typeof SeatOfferKindSchema>;

/** Почему место нельзя занять прямо сейчас (подписи — в `texts.ts`). */
export const SeatBlockReasonSchema = z.enum([
  'offered_to_other',
  'already_member',
  'already_in_squad',
  'level_mismatch',
  'other_city',
  'onboarding_required',
  /** Пробный на этот сбор уже взят (живой заказ `TRIAL`: оплачен или ждёт оплаты) */
  'trial_used',
  'squad_full',
  /** Раунд персон 1, N3: человек уже на этом сборе (запись явки: замена, пробный, разовое место) */
  'already_going',
]);
export type SeatBlockReason = z.infer<typeof SeatBlockReasonSchema>;

export const SeatItemSchema = z.object({
  kind: SeatOfferKindSchema,
  /** id `OpenSeat`; у пробного сбора места нет — покупка идёт по `gatheringId` */
  seatId: IdSchema.nullable(),
  squadId: IdSchema,
  squadName: z.string().nullable(),
  activity: ActivityBriefSchema,
  cityName: z.string(),
  districtNames: z.array(z.string()),
  weekday: WeekdaySchema,
  startTime: z.string(),
  levelBand: LevelSchema,
  capacity: z.number().int().positive(),
  /** Сколько человек уже в составе */
  membersCount: z.number().int().nonnegative(),
  /** Разовое место: конкретный сбор */
  gatheringId: IdSchema.nullable(),
  startsAt: z.string().datetime().nullable(),
  venueName: z.string().nullable(),
  /** Адрес виден только после оплаты (Stage 11, задача 2) */
  venueAddress: z.string().nullable(),
  remainingGatherings: z.number().int().nonnegative(),
  totalGatherings: z.number().int().nonnegative(),
  priceKopecks: KopecksSchema,
  /** Место предложено лично мне — до этого момента оно моё */
  offeredToMe: z.boolean(),
  offerExpiresAt: z.string().datetime().nullable(),
  canClaim: z.boolean(),
  blockReason: SeatBlockReasonSchema.nullable(),
  /**
   * Stage 17: формат турнира сбора (`tournamentFormatOf`) — по нему клиент показывает
   * предупреждение перед записью; null — не турнир или место не на один сбор
   */
  tournamentFormat: TournamentFormatSchema.nullable().default(null),
  /** Длительность сбора, минуты: время окончания турнира в предупреждении */
  durationMin: z.number().int().positive().nullable().default(null),
  /** Таймзона города отряда: дата и время окончания показываются в ней */
  timezone: z.string().nullable().default(null),
  /**
   * Раунд персон 1 (M2/N1): сбор уже лежит в живой брони человека — id заявки; витрина пишет
   * «В твоей брони» со ссылкой на заявку вместо «Записаться» (`canClaim = false`)
   */
  inBookingId: IdSchema.nullable().default(null),
});
export type SeatItem = z.infer<typeof SeatItemSchema>;

export const OpenSeatsListInputSchema = z.object({
  cityId: IdSchema.optional(),
  kind: z.array(SeatOfferKindSchema).max(5).optional(),
});
export type OpenSeatsListInput = z.infer<typeof OpenSeatsListInputSchema>;

/** Живая бронь человека на витрине (раунд персон 1): новая заявка дописывается в неё. */
export const ShowcaseLiveBookingSchema = z.object({
  id: IdSchema,
  itemsCount: z.number().int().nonnegative(),
  holdUntil: z.string().datetime(),
});
export type ShowcaseLiveBooking = z.infer<typeof ShowcaseLiveBookingSchema>;

/**
 * Лимит без сезона для витрины (раунд персон 1, N5): клиент считает счётчик и блокирует карточки
 * той же функцией `noSeasonLimitReached`, что сервер, с учётом своего выбора. null — у человека
 * оплаченный сезон или «Мои сборы» выключены: лимита нет.
 */
export const NoSeasonLimitStateSchema = z.object({
  max: z.number().int().nonnegative(),
  windowDays: z.number().int().nonnegative(),
  /** Начала сборов уже взятых позиций (оплаченных и в живой брони, не возвращённых) */
  existing: z.array(z.string().datetime()),
});
export type NoSeasonLimitState = z.infer<typeof NoSeasonLimitStateSchema>;

export const OpenSeatsListResultSchema = z.object({
  items: z.array(SeatItemSchema),
  /**
   * Фиче-флаги `feature.openSeats`, `feature.trial` и (Stage 17) `feature.myGatherings`: с ним
   * разовые места, замены и пробные набираются в заявку, а не покупаются по одному
   */
  features: z.object({
    openSeats: z.boolean(),
    trial: z.boolean(),
    myGatherings: z.boolean().default(false),
  }),
  trialPriceKopecks: KopecksSchema,
  /** Сколько сборов помещается в одну заявку (`booking.maxItems`) — предел выбора на клиенте */
  bookingMaxItems: z.number().int().positive().default(BOOKING_DEFAULTS.maxItems),
  /** Сколько дней после оплаты разовое место и замена засчитываются в сезон (`pricing.singleCreditDays`) */
  singleCreditDays: z.number().int().nonnegative().default(BOOKING_DEFAULTS.singleCreditDays),
  /** Раунд персон 1: живая бронь человека (`feature.myGatherings`); null — брони нет */
  liveBooking: ShowcaseLiveBookingSchema.nullable().default(null),
  noSeasonLimit: NoSeasonLimitStateSchema.nullable().default(null),
});
export type OpenSeatsListResult = z.infer<typeof OpenSeatsListResultSchema>;

export const OpenSeatClaimInputSchema = z.object({ seatId: IdSchema });
export type OpenSeatClaimInput = z.infer<typeof OpenSeatClaimInputSchema>;

/** «Найти запасного в городе» на `/squad/reserve` (задача 1a). */
export const ReserveOpenToPoolInputSchema = z.object({ squadId: IdSchema.optional() });
export type ReserveOpenToPoolInput = z.infer<typeof ReserveOpenToPoolInputSchema>;

// ───────────────────────────── админка ─────────────────────────────

export const AdminSeatsListInputSchema = z.object({
  status: z.array(OpenSeatStatusSchema).max(5).optional(),
  kind: z.array(OpenSeatKindSchema).max(Object.keys(OpenSeatKind).length).optional(),
  squadId: IdSchema.optional(),
  cursor: IdSchema.optional(),
  limit: z.number().int().min(1).max(100).default(50),
});
export type AdminSeatsListInput = z.infer<typeof AdminSeatsListInputSchema>;

/** Ручное предложение места конкретному пользователю (задача 5). */
export const AdminSeatOfferInputSchema = z.object({ seatId: IdSchema, userId: IdSchema });
export type AdminSeatOfferInput = z.infer<typeof AdminSeatOfferInputSchema>;

export const AdminSeatCancelInputSchema = z.object({
  seatId: IdSchema,
  reason: z.string().trim().max(300).nullable().default(null),
});
export type AdminSeatCancelInput = z.infer<typeof AdminSeatCancelInputSchema>;

export const AdminSeatCandidatesInputSchema = z.object({
  seatId: IdSchema,
  limit: z.number().int().min(1).max(50).default(20),
});
export type AdminSeatCandidatesInput = z.infer<typeof AdminSeatCandidatesInputSchema>;
